import { InjectionToken, makeEnvironmentProviders, type EnvironmentProviders } from '@angular/core';
import type { AuroraWidgetDef } from './aurora-widget-def';

/**
 * Multi-provider token that aggregates `AuroraWidgetDef` entries from every
 * bounded context. Inject with `optional: true` and apply `?? []` to handle the
 * zero-registration case without error.
 *
 * Design decision D3: modeled exactly on `AUTHENTICATED_INITIALIZER` — an
 * `InjectionToken` + `multi: true` + a `provideX(...)` function. This is the
 * established idiom (auth initializer, validation messages) and requires no
 * imperative registration step.
 *
 * @example
 * ```typescript
 * const widgets = inject(AURORA_WIDGET, { optional: true }) ?? [];
 * ```
 */
export const AURORA_WIDGET = new InjectionToken<ReadonlyArray<AuroraWidgetDef>>(
  'AURORA_WIDGET',
);

/**
 * Register one or more `AuroraWidgetDef` entries with the platform registry.
 * Call this in a BC's `providers` array (or in the root `app.config.ts` spread),
 * exactly as `provideWidget(...hubWidgets)`.
 *
 * @example
 * ```typescript
 * // hub.widgets.ts
 * export const hubWidgets = provideWidget(launcherWidget, statsWidget);
 * ```
 */
export function provideWidget(...widgets: AuroraWidgetDef[]): EnvironmentProviders {
  return makeEnvironmentProviders(
    widgets.map((widget) => ({ provide: AURORA_WIDGET, useValue: widget, multi: true })),
  );
}
